import React from 'react';
import { Tag, Store, FolderTree, Folder, Layers, Filter, ChevronDown, ChevronUp, Bookmark, X } from 'lucide-react';
import FilterDropdown, { FilterDropdownMeta } from './FilterDropdown';
import {
  FilterBoxConfig,
  DimensionKey,
  FilterControlKey,
  TREE_LEVEL_ORDER,
  DIMENSIONS_WITH_NO_OPTIONS,
  isDimensionKey,
  resolveDimensionLabels,
} from '../../config/filterBoxConfigs';

/**
 * FilterBox — la ÚNICA caja de filtros reusable (GH#24).
 *
 * Compone N `FilterDropdown` (dimensionales) según una `FilterBoxConfig` declarativa,
 * más los controles NO dimensionales (precio/posición/descuento/búsqueda) que el host
 * aporta como `slots`. La consumen Publicaciones (vía UnifiedFilterPanel), Estadísticas
 * y Data Health — cada una con su propia config y su propio state.
 *
 * - El state por dimensión (filter/applied/pendingRemove + setters + options/meta) lo
 *   inyecta el host en `dimensions`. Para consumidores nuevos, `useFilterBoxState` lo
 *   arma; Publicaciones reusa el state que ya vive en Dashboard.
 * - `enabled` por dimensión = clientAllows AND sectionConfig.enabled (regla de
 *   composición, spec §3). Si una dimensión no está en `dimensions` o viene con
 *   `enabled=false`, no se renderiza (el cliente no la tiene / la sección la apagó).
 * - `treeMode` (árbol por datos, spec §4.5): al haber selección en un nivel padre, las
 *   options del nivel hijo se acotan a las que co-ocurren (meta.counts > 0). Barato,
 *   multi-cliente (Adidas colapsa el nivel ausente por caps), sin tocar schema.
 */

export interface DimensionState {
  filter: string[];
  applied: string[];
  pendingRemove: string[];
  setFilter: (value: string[]) => void;
  setPendingRemove?: (value: string[]) => void;
  options: string[];
  meta?: FilterDropdownMeta | null;
  /** clientAllows AND sectionConfig.enabled. Si false → no se renderiza. */
  enabled: boolean;
  /** Auto-load de options al abrir (lo que hacía el panel viejo). */
  onOpen?: () => void;
  /**
   * Quita una opción YA APLICADA de forma INMEDIATA (destildar = misma acción que la X
   * de la pastilla). Sin esto, destildar una aplicada caía en el patrón pendingRemove
   * (strikethrough hasta Filtrar), que confundía al usuario.
   */
  onRemoveApplied?: (value: string) => void;
}

/** Render de un control NO dimensional (precio/posición/descuento/búsqueda/extras). */
export type FilterSlot = (widthStyle?: React.CSSProperties) => React.ReactNode;

/**
 * PT-#45 #5 — Chip de "filtros aplicados" NO dimensional (rango de precio/posición/
 * descuento/búsqueda). Los dimensionales los deriva FilterBox solo desde `dimensions`;
 * el host aporta acá los suyos para no duplicar la fuente de verdad del state numérico.
 */
export interface FilterChip {
  id: string;
  label: string;
  onRemove: () => void;
  /**
   * PT-#57 R2-5 — chip de selección EN VIVO (valor tildado pero aún no aplicado): se
   * muestra distinto (ámbar) para dar feedback inmediato al seleccionar, antes de Filtrar.
   */
  pending?: boolean;
}

export interface FilterBoxProps {
  config: FilterBoxConfig;
  /** Función de traducción (i18next `t`), con fallback. */
  t: (key: string, fallback?: string) => string;
  /** State + options por dimensión. Ausente/enabled=false ⇒ no se renderiza. */
  dimensions: Partial<Record<DimensionKey, DimensionState>>;
  /** Slots para los controles no dimensionales presentes en la config. */
  slots?: Partial<Record<FilterControlKey, FilterSlot>>;
  /** Recalculo de counts en curso (faceted refresh) → dim + spinner en cada dropdown. */
  loading?: boolean;

  // ── PT-#45 #2 — Banda de título DENTRO del control (compartida por las 3 secciones) ──
  /** Si true, FilterBox rinde su propia banda "Filtros" + acciones + colapso. */
  showHeader?: boolean;
  /** Texto del título de la banda (default: t('filters.title')). */
  headerTitle?: string;
  /** Badge del filtro guardado activo (ej. "<nombre> *"), a la derecha del título. */
  activeFilterBadge?: React.ReactNode;
  /** Abre el modal de filtros guardados. Si se omite, no se muestra el botón. */
  onOpenSavedFilters?: () => void;
  /** Deshabilita el botón de guardados mientras cargan. */
  savedFiltersLoading?: boolean;
  /** Estado de colapso del cuerpo (controlado por el host). */
  collapsed?: boolean;
  /** Toggle de colapso. Si se omite, no se muestra el botón mostrar/ocultar. */
  onToggleCollapse?: () => void;

  // ── PT-#45 #5 — Caja "Filtros aplicados" (pills removibles) ──
  /** Chips NO dimensionales (rango de precio/posición/descuento/búsqueda) del host. */
  appliedChips?: FilterChip[];
  /** Slot fijo a la derecha de la fila de pills (ej. botón "Filtrar"). */
  actionsSlot?: React.ReactNode;
  /**
   * PT-#57 bug2 — "Limpiar todo": resetea TODOS los filtros (dimensionales + numéricos +
   * búsqueda). Se muestra dentro del panel "Filtros aplicados" (a la derecha del título)
   * cuando hay al menos un filtro aplicado. Reemplaza la caja de aplicados externa/redundante.
   */
  onClearAll?: () => void;
}

// Selección efectiva de una dimensión = (filter ∪ applied) menos pendingRemove.
const hasEffectiveSelection = (ds?: DimensionState): boolean => {
  if (!ds) return false;
  const pending = new Set(ds.pendingRemove || []);
  const eff = new Set<string>();
  ds.filter.forEach(v => eff.add(v));
  ds.applied.forEach(v => { if (!pending.has(v)) eff.add(v); });
  ds.filter.forEach(v => { if (pending.has(v)) eff.delete(v); });
  return eff.size > 0;
};

/**
 * treeMode: cadena de niveles PRESENTES (según `dimensions`), en orden jerárquico.
 * Adidas (sin subcategory) ⇒ [category, segment]; cliente sin sub/seg ⇒ [category].
 */
const presentTreeChain = (dimensions: FilterBoxProps['dimensions']): DimensionKey[] =>
  TREE_LEVEL_ORDER.filter(k => dimensions[k] && dimensions[k]!.enabled);

/**
 * Options visibles de una dimensión bajo treeMode: si su padre inmediato (en la cadena
 * presente) tiene selección efectiva, acotar a las options con meta.counts > 0.
 */
function treeVisibleOptions(
  key: DimensionKey,
  ds: DimensionState,
  dimensions: FilterBoxProps['dimensions'],
): string[] {
  const chain = presentTreeChain(dimensions);
  const idx = chain.indexOf(key);
  if (idx <= 0) return ds.options; // no es nivel-árbol o es la raíz (Categoría)
  // PT-#57 R2-4 — acotar si CUALQUIER ancestro (no solo el padre inmediato) tiene selección
  // efectiva. Así SEGMENTO se acota al elegir CATEGORÍA aunque no haya SUBCATEGORÍA elegida
  // (no debe poder elegirse un segmento incompatible con la categoría, igual que Pubs/Stats).
  // El facet ya cross-filtra por todos los ancestros → meta.counts refleja la co-ocurrencia.
  const anyAncestorSelected = chain
    .slice(0, idx)
    .some(k => hasEffectiveSelection(dimensions[k]));
  if (!anyAncestorSelected) return ds.options; // ningún ancestro con selección → sin acotar
  if (!ds.meta) return ds.options; // sin counts todavía → no filtrar (evita ocultar de más)
  const counts = ds.meta.counts || {};
  const selectedSet = new Set([...ds.filter, ...ds.applied]); // nunca ocultar lo ya elegido
  return ds.options.filter(opt => (counts[opt] || 0) > 0 || selectedSet.has(opt));
}

// PT-#45 #16 — ícono por dimensión a la izquierda del label (consistente en las 4 secciones).
const DIMENSION_ICONS: Record<DimensionKey, React.ReactNode> = {
  brand: <Tag className="h-3.5 w-3.5" />,
  site: <Store className="h-3.5 w-3.5" />,
  category: <FolderTree className="h-3.5 w-3.5" />,
  subcategory: <Folder className="h-3.5 w-3.5" />,
  segment: <Layers className="h-3.5 w-3.5" />,
};

/**
 * Chips dimensionales derivados de `dimensions`: por cada dimensión habilitada, una pill
 * por valor efectivamente aplicado (applied menos pendingRemove). Al quitarla se usa
 * `onRemoveApplied` (misma acción que la X de la pastilla del dropdown / destildar).
 * Fuente única de verdad: el state ya vive en `dimensions`, no se duplica.
 */
function dimensionChips(
  dimensions: FilterBoxProps['dimensions'],
  t: FilterBoxProps['t'],
): FilterChip[] {
  const chips: FilterChip[] = [];
  (Object.keys(dimensions) as DimensionKey[]).forEach(key => {
    const ds = dimensions[key];
    if (!ds || !ds.enabled) return;
    const pendingRemove = new Set(ds.pendingRemove || []);
    const appliedSet = new Set(ds.applied);
    const { label } = resolveDimensionLabels(key, t);
    // Aplicados (menos los marcados para quitar): pastilla azul.
    ds.applied.forEach(value => {
      if (pendingRemove.has(value)) return; // marcada para quitar → no cuenta como aplicada
      chips.push({
        id: `dim-${key}-${value}`,
        label: `${label}: ${value}`,
        onRemove: () => {
          if (ds.onRemoveApplied) ds.onRemoveApplied(value);
          else ds.setFilter(ds.applied.filter(v => v !== value));
        },
      });
    });
    // PT-#57 R2-5 — selección EN VIVO: valores tildados aún no aplicados (en filter, no en
    // applied). Pastilla ámbar (pending). Al quitarla se destilda del filter pendiente.
    ds.filter.forEach(value => {
      if (appliedSet.has(value)) return; // ya está como aplicado (azul), no duplicar
      chips.push({
        id: `dim-pending-${key}-${value}`,
        label: `${label}: ${value}`,
        pending: true,
        onRemove: () => ds.setFilter(ds.filter.filter(v => v !== value)),
      });
    });
  });
  return chips;
}

const FilterBox: React.FC<FilterBoxProps> = ({
  config,
  t,
  dimensions,
  slots = {},
  loading = false,
  showHeader = false,
  headerTitle,
  activeFilterBadge,
  onOpenSavedFilters,
  savedFiltersLoading = false,
  collapsed = false,
  onToggleCollapse,
  appliedChips = [],
  actionsSlot,
  onClearAll,
}) => {
  const treeMode = !!config.treeMode;
  const allChips: FilterChip[] = [...dimensionChips(dimensions, t), ...appliedChips];

  const renderDimension = (key: DimensionKey, widthStyle?: React.CSSProperties) => {
    const ds = dimensions[key];
    if (!ds || !ds.enabled) return null; // cliente no la tiene / sección la apagó
    const { label, labels } = resolveDimensionLabels(key, t);
    const options = treeMode ? treeVisibleOptions(key, ds, dimensions) : ds.options;
    // PT-#57 R2-4 — Tendencias MUESTRA counts igual que Pubs/Stats (hideCounts revertido):
    // el count del facet real de la toma es válido y da la señal de cascade que el usuario
    // espera. `hideCounts` queda como capacidad genérica del config (hoy sin uso).
    const displayMeta = config.hideCounts ? null : ds.meta;
    // PT-#57 R2-4 — el spinner "Actualizando" solo va en las dimensiones que REALMENTE
    // cascadean (las que el host alimenta con facet meta). En Pubs/Stats todas tienen meta;
    // en Trends solo Categoría/Subcategoría/Segmento (Marca/Sitio son meta:null ⇒ no refrescan
    // counts, no deben mostrar loading).
    const dimLoading = loading && !!ds.meta;
    return (
      <FilterDropdown
        key={key}
        label={label}
        icon={DIMENSION_ICONS[key]}
        options={options}
        meta={displayMeta}
        filter={ds.filter}
        applied={ds.applied}
        pendingRemove={ds.pendingRemove}
        setFilter={ds.setFilter}
        setPendingRemove={ds.setPendingRemove}
        onRemoveApplied={ds.onRemoveApplied}
        labels={labels}
        showNoOptions={DIMENSIONS_WITH_NO_OPTIONS.includes(key)}
        onOpen={ds.onOpen}
        widthStyle={widthStyle}
        loading={dimLoading}
      />
    );
  };

  const renderField = (key: FilterControlKey, widthPct?: number): React.ReactNode => {
    // PT-#45 #1 — Grilla 4/2/1. La fila es `flex gap-4 flex-wrap` (gap=1rem). Un ancho de
    // `width: X%` puro NO deja lugar al gap, así que N ítems de X%=100/N desbordan el 100%
    // y flex-wrap los baja de a uno (numéricos quedaban 1-por-fila en vez de 2). Restar el
    // gap (`calc(X% - 1rem)`) hace que las N columnas entren en la fila; el minWidth sigue
    // forzando el reflow en anchos chicos.
    const widthStyle: React.CSSProperties | undefined =
      widthPct != null ? { width: `calc(${widthPct}% - 1rem)`, minWidth: '180px' } : undefined;
    if (isDimensionKey(key)) return renderDimension(key, widthStyle);
    const slot = slots[key];
    return slot ? <React.Fragment key={key}>{slot(widthStyle)}</React.Fragment> : null;
  };

  // Cuerpo de la caja: las filas de controles (dimensionales + slots) según la config.
  const body = (
    <div className="space-y-4">
      {config.rows.map((row, rowIdx) => {
        const enabledFields = row.fields.filter(f => f.enabled);
        // Un renglón entero se apaga si ninguno de sus fields renderea algo.
        const rendered = enabledFields
          .map(f => renderField(f.key, f.widthPct))
          .filter(Boolean);
        if (rendered.length === 0) return null;
        return (
          <div key={rowIdx} className="flex gap-4 flex-wrap">
            {rendered}
          </div>
        );
      })}
    </div>
  );

  // PT-#45 #5 — Caja "Filtros aplicados": pills removibles + slot de acción (botón Filtrar).
  const appliedBar = (allChips.length > 0 || actionsSlot) ? (
    <div className="flex items-stretch gap-3 pt-3 border-t border-gray-200">
      {/* PT-#57 item2 — la caja de pastillas ocupa ~85% (flex-1); el botón "Filtrar" va a
          la derecha, centrado verticalmente a la MISMA ALTURA de la caja (items-stretch +
          items-center en el wrapper del botón), sin el offset pt-5 que lo desalineaba. */}
      <div className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-white/60 p-2.5">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
            <Filter className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <span>{t('filters.applied_filters', 'Filtros aplicados')}</span>
          </div>
          {/* PT-#57 bug2 — "Limpiar todo" dentro del panel de aplicados (reemplaza la caja externa). */}
          {onClearAll && allChips.length > 0 && (
            <button
              type="button"
              onClick={onClearAll}
              className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-red-600 transition-colors flex-shrink-0"
              title={t('filters.clear_all', 'Limpiar todo')}
            >
              <X className="h-3.5 w-3.5" />
              <span>{t('filters.clear_all', 'Limpiar todo')}</span>
            </button>
          )}
        </div>
        {allChips.length > 0 ? (
          /* PT-#57 item B — tope de alto ~150px (≈3 líneas de pastillas): al pasarse,
             scroll vertical en vez de empujar/romper la página. */
          <div className="flex flex-wrap gap-2 max-h-[150px] overflow-y-auto pr-1">
            {allChips.map(chip => (
              <span
                key={chip.id}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${
                  chip.pending
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-blue-50 text-blue-700 border-blue-200'
                }`}
              >
                <span className="max-w-[220px] truncate">{chip.label}</span>
                <button
                  type="button"
                  onClick={chip.onRemove}
                  className="hover:opacity-70 transition-opacity flex-shrink-0"
                  title={chip.label}
                  aria-label={`Quitar filtro: ${chip.label}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-gray-400">{t('filters.no_active_filters', 'Sin filtros aplicados')}</span>
        )}
      </div>
      {actionsSlot && <div className="flex-shrink-0 flex items-center">{actionsSlot}</div>}
    </div>
  ) : null;

  // Sin banda de título: layout histórico (Tendencias / preview del config lo consumen así).
  if (!showHeader) {
    return (
      <div className="space-y-4">
        {body}
        {appliedBar}
      </div>
    );
  }

  // PT-#45 #2 — Banda de título "Filtros" DENTRO del control, compartida por las 3 secciones.
  const showToggle = !!onToggleCollapse;
  const showSaved = !!onOpenSavedFilters;
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white rounded-t-lg">
        <div
          className={`flex items-center space-x-2 ${showToggle ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
          onClick={showToggle ? onToggleCollapse : undefined}
        >
          <Filter className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-800">{headerTitle ?? t('filters.title', 'Filtros')}</h3>
          {activeFilterBadge}
        </div>

        <div className="flex items-center space-x-2">
          {showSaved && (
            <button
              type="button"
              onClick={onOpenSavedFilters}
              disabled={savedFiltersLoading}
              className="flex items-center space-x-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
            >
              <Bookmark className="h-4 w-4" />
              <span>{t('filters.saved_filters.title', 'Filtros Guardados')}</span>
            </button>
          )}
          {showToggle && (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <span>{collapsed ? t('filters.show_filters', 'Mostrar filtros') : t('filters.hide_filters', 'Ocultar filtros')}</span>
              {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>

      <div
        className={`transition-all duration-300 ease-in-out ${
          collapsed ? 'max-h-0 opacity-0 overflow-hidden' : 'max-h-[2000px] opacity-100'
        }`}
      >
        <div className="p-4 bg-gray-50 border-t border-gray-200 space-y-4">
          {body}
          {appliedBar}
        </div>
      </div>
    </div>
  );
};

export default FilterBox;
